import React, { useEffect, useState } from "react";
import { Modal, Radio, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/Button";
import PDFBadge from "components/icons/PDFBadge";
import DOCBadge from "components/icons/DOCBadge";
import PILBadge from "components/icons/PILBadge";
import DownloadIcon from "components/icons/DownArrowIcon";
import DeleteIcon from "components/icons/Delete";
import SuccessMessage from "utils/SuccessMessage";
import ErrorMessage from "utils/ErrorMessage";
import styled from "styled-components";
import { uploadFile, deleteFile } from "modules/Onboarding/actions";
import { upsertStudentResume } from "modules/Resume/actions";
import moment from "moment";
import ResumeDownload from "modules/Resume/Components/ResumeDownload";
import { getPendingData } from "../actions";
import {
  DrawerListWrapper,
} from "modules/Resume/DrawerComponents/DrawerStyle";

import {
  ResumeCard,
  ResumeLeft,
  ResumeFileName,
  ResumeDate,
  ResumeActions,
  ResumeLimitNote,
  UploadButtonWrapper,
} from "modules/Resume/Style/ResumeStyle";

const MAX_FILES = 6;
const MAX_SIZE_MB = 5;

// ------------------------------
// WHITE MODAL HEADER OVERRIDE
// ------------------------------
const WhiteModal = styled(Modal)`
  .ant-modal-header {
    background: #fff !important;
    border-bottom: 1px solid #E5E7EB !important;
    border-radius: 12px 12px 0 0 !important;
  }

  .ant-modal-title {
    background: #fff !important;
  font-size: 20px;
  color: #1E1E1E;
  }

  .ant-modal-content {
    border-radius: 12px !important;
    overflow: hidden;
  }

  .ant-modal-close {
    background: #fff !important;
    border-radius: 50%;
  }
`;

const ApplyResumePopup = ({
  visible,
  onClose,
  resumeFiles = [],
  defaultId = "PIL",
  onApply,
  getStudentResumeData,
  studentId,
  studentUpdateAt,
  studentDetails,
}) => {
const pendingData = useSelector(
  state => state?.resumeData?.pendingData
);
  const dispatch = useDispatch();
  const [selectedId, setSelectedId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [localFiles, setLocalFiles] = useState([]);
  const [loadingResumes, setLoadingResumes] = useState(false);

  /** Reset on open */
  useEffect(() => {
  if (visible) {
    fetchResumeData();
    setError("");
  }
}, [visible,pendingData]);

const mapPendingResumesToUi = (pendingData) => {
  const pendingResumes = pendingData?.resumes || [];

  return pendingResumes.map(r => ({
    id: r._itemId || `pending-${r._requestId}`,
    name: r.resumeName,
    type: "UPLOADED",
    url: r.fileUrl?.url || "",
    uploadedOn: new Date(r.createdAt || r.uploadedAt || Date.now()).toLocaleDateString("en-GB"),
    isDefault: false,
    fileFormat: r.fileFormat?.toLowerCase(),
    status: "PENDING",
    _raw: r,
  }));
};


const mapApiResumesToUi = (backendResumes = []) =>
  backendResumes.map((r) => ({
    id: r.id,
    name: r.resumeName,
    type: r.resumeType === "SYSTEM" ? "SYSTEM" : "UPLOADED",
    url: r.fileUrl?.url || "",
    uploadedOn: new Date(r.createdAt).toLocaleDateString("en-GB"),
    isDefault: r.isDefault,
    fileFormat: r.fileFormat?.toLowerCase() || null,
    _raw: r,
  }));

const fetchResumeData = async () => {
  setLoadingResumes(true);

  try {
    // 1️⃣ Approved resumes
    const result = await getStudentResumeData(studentId);
    const apiResumes = result?.resumes || result?.data?.resumes || [];
    const approved = mapApiResumesToUi(apiResumes);

    // 2️⃣ Pending resumes (Redux)
    const pending = mapPendingResumesToUi(pendingData);

    // 3️⃣ Remove duplicates by URL
    const approvedUrls = new Set(approved.map(r => r.url));
    const filteredPending = pending.filter(
      p => !approvedUrls.has(p.url)
    );

    // 4️⃣ Merge
    setLocalFiles([...approved, ...filteredPending]);
    setSelectedId(null);
  } catch (e) {
    console.error("Failed to load resumes:", e);
  } finally {
    setLoadingResumes(false);
  }
};

  /** Upload handler */
const handleUpload = async (event) => {
  setLoadingResumes(true);
  const file = event.target.files[0];
  if (!file) return;

  const ext = file.name.split('.').pop().toLowerCase();
  const validExts = ['pdf'];

  if (!validExts.includes(ext)) {
    setError("Only PDF files are allowed");
    event.target.value = "";
    return;
  }

  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    setError(`File size should not exceed ${MAX_SIZE_MB}MB`);
    event.target.value = "";
    return;
  }

  if (localFiles.length >= MAX_FILES) {
    setError(`Maximum ${MAX_FILES} files allowed`);
    event.target.value = "";
    return;
  }

  setError("");

  try {
    // 1️⃣ Upload to S3
    const uploadedFileUrl = await dispatch(
      uploadFile(file, `resume_${Date.now()}.${ext}`)
    );

    if (!uploadedFileUrl) throw new Error("File upload failed");

    // 2️⃣ Get latest resumes from backend
    // approved + system
    const result = await getStudentResumeData(studentId);
    const existing = result?.resumes || result?.data?.resumes || [];

    // pending (from redux)
    const pending = (pendingData?.resumes || []).map(p => ({
      id: null,
      resumeName: p.resumeName,
      resumeType: "UPLOADED",
      fileUrl: { url: p.fileUrl?.url },
      isDefault: false,
    }));
    console.log("pending data",pending)

    // 3️⃣ Append new resume to existing list
    const updatedResumes = [
      ...existing,
      ...pending,
      {
        id: null,
        resumeName: file.name,
        resumeType: "UPLOADED",
        fileUrl: { url: uploadedFileUrl },
        isDefault: false,
        fileSize: file.size,
      },
    ];

    // 4️⃣ Push full array to backend
    await dispatch(
      upsertStudentResume({
        resumes: updatedResumes,
          checkForApproval: true,
    instituteCampusId: studentDetails?.student?.instituteCampusId
      })
    );

    await dispatch(getPendingData());
    // 5️⃣ Refresh UI
  } catch (error) {
    console.error("Upload error:", error);
    ErrorMessage("Failed to upload resume");
  } finally {
    event.target.value = "";
    setLoadingResumes(false);
  }
};

  /** Delete file */
  // const handleDelete = async (id) => {
  //   const fileToDelete = localFiles.find(f => f.id === id);
  //   if (!fileToDelete) return;
  //   try {
  //     setLoading(true);      
  //     // If it's a server-uploaded file (has a URL), delete it from the server
  //     if (fileToDelete.url && !fileToDelete.url.startsWith('blob:')) {
  //       const success = await dispatch(deleteFile(fileToDelete.url));
  //       if (!success) {
  //         throw new Error('Failed to delete file from server');
  //       }
  //     }
  //     // Update local state
  //     setLocalFiles((prev) => {
  //       const updated = prev.filter((f) => f.id !== id);
  //       // If deleted file was selected, select another one
  //       if (selectedId === id) {
  //         setSelectedId(updated.length > 0 ? updated[0].id : file.id);
  //       }
  //       return updated;
  //     });
  //     SuccessMessage("Resume deleted successfully!");
  //   } catch (error) {
  //     console.error('Delete error:', error);
  //     ErrorMessage('Failed to delete resume');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  /** Download file */
      const downloadURI = async (file) => {
      // Create a blob from the URL to force download
      try {
        const response = await fetch(file.url)
        if (!response.ok) {
          throw new Error('Failed to download file')
        }
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = file.name
        document.body.appendChild(a)
        a.click()
        URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        console.error('Error downloading file:', error)
      }
    }

  /** Render user resumes */
const renderUserFiles = () =>
  localFiles.map((file) => {
    const isSelected = selectedId === file.id;
    const isPdf = file.fileFormat === "pdf" ;

    if (file.type !== "SYSTEM") {
      return (
        <ResumeCard
          key={file.id}
          style={{
            cursor: file.status === "PENDING" ? "not-allowed" : "pointer",
            background: isSelected ? "#DAECFF" : "#FFFFFF",
          }}
            onClick={() => { 
              if (file.status === "PENDING") return;
            setSelectedFile(file)
            setSelectedId(file.id)}}
        >
          <ResumeLeft>
            {isPdf ? <PDFBadge /> : <DOCBadge />}
            <div>
              <ResumeFileName>{file.name}</ResumeFileName>
              <ResumeDate>Uploaded on {file.uploadedOn}</ResumeDate>
            </div>
          </ResumeLeft>

          <ResumeActions>
            {file.isDefault && (
  <div
    style={{
      width: 67,
      height: 26,
      borderRadius: 5,
      background: "#F2F7FF",
      border: "1px solid #DAE6FF",
      color: "#3A66FF",
      fontSize: 12,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 10,
    }}
  >
    Default
  </div>
)}
{file.status === "PENDING" && (
  <div
    style={{
      padding: "4px 10px",
      borderRadius: 5,
      background: "#FFF7E6",
      border: "1px solid #FFD591",
      color: "#D46B08",
      fontSize: 12,
      fontWeight: 500,
      marginRight: 10,
    }}
  >
    Pending approval
  </div>
)}

            <span
              onClick={(e) => {
                e.stopPropagation();
                downloadURI(file);
              }}
            >
              <DownloadIcon />
            </span>
            {/* <span
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(file.id);
              }}
            >
              <DeleteIcon />
            </span> */}
<Radio
  disabled={file.status === "PENDING"}
  checked={selectedId === file.id}
  onChange={() => {
    if (file.status === "PENDING") return;
    setSelectedFile(file);
    setSelectedId(file.id);
  }}
/>
          </ResumeActions>
        </ResumeCard>
      );
    } else {
      return (
        <ResumeCard
          key={file.id}
          style={{
            cursor: file.status === "PENDING" ? "not-allowed" : "pointer",
            background: isSelected ? "#DAECFF" : "#FFFFFF",
          }}
          onClick={() => { 
            if (file.status === "PENDING") return;
            setSelectedFile(file)
            setSelectedId(file.id)}}
        >
          <ResumeLeft>
            <PILBadge />
            <div>
              <ResumeFileName>PIL Resume</ResumeFileName>
              <ResumeDate>Updated on {moment(file._raw.updateAt).format("DD/MM/YYYY")}</ResumeDate>
            </div>
          </ResumeLeft>

          <ResumeActions>
             {file.isDefault && (
  <div
    style={{
      width: 67,
      height: 26,
      borderRadius: 5,
      background: "#F2F7FF",
      border: "1px solid #DAE6FF",
      color: "#3A66FF",
      fontSize: 12,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 10,
    }}
  >
    Default
  </div>
)}
            <div
              style={{
                padding: "4px 10px",
                borderRadius: "999px",
                background: "#EEF0FF",
                fontSize: 11,
                fontWeight: 600,
                color: "#3249D7",
              }}
            >
              System Generated
            </div>
            <ResumeActions>
              {studentDetails ? (
                <ResumeDownload studentData={studentDetails}>
                  {({ url, fileName }) => (
                    <a
                      href={url || undefined}
                      download={fileName}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <DownloadIcon />
                    </a>
                  )}
                </ResumeDownload>
              ) : (
                <span onClick={null}>
                  <DownloadIcon />
                </span>
              )}
            </ResumeActions>
<Radio
  disabled={file.status === "PENDING"}
  checked={selectedId === file.id}
  onChange={() => {
    if (file.status === "PENDING") return;
    setSelectedFile(file);
    setSelectedId(file.id);
  }}
/>
          </ResumeActions>
        </ResumeCard>
      );
    }
  });

 

  /** Popup Content */
  const content = (
    <>
      <DrawerListWrapper>
        {loadingResumes ? <Spin size="large" style={{ textAlign:"center", padding:40 }} /> : (
  <>
    {renderUserFiles()}
  </>
)}
      </DrawerListWrapper>

      {/* Hidden upload input */}
      <input
        id="upload-resume-input-popup"
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onChange={handleUpload}
      />

      <div style={{ marginTop: 16 }}>
        {localFiles.length < MAX_FILES ? (
          <UploadButtonWrapper>
            <Button.Secondary
              text="Upload New Resume"
              onClick={() =>
                document.getElementById("upload-resume-input-popup").click()
              }
            />
          </UploadButtonWrapper>
        ) : (
          <ResumeLimitNote>
            You’ve reached the limit of 5 resumes.To upload a new file, please remove an existing resume from your Profile Section.
          </ResumeLimitNote>
        )}
      </div>

      {error && (
        <div style={{ color: "red", marginTop: 8, fontSize: 13 }}>{error}</div>
      )}
    </>
  );

  return (
    <WhiteModal
      open={visible}
      onCancel={onClose}
      closable={true}
      footer={null}
      width={720}
      title={
  <span>
    <span style={{ fontWeight: 500 }}>Select Resume</span>
    <span style={{ fontWeight: 350, fontSize: 18 }}> – Be sure to include an updated resume</span>
  </span>
}
    >
      {content}

      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginTop: 24,
        }}
      >
        <Button.Secondary text="Cancel" onClick={onClose} />
        <Button.Primary
          text="Apply"
          onClick={() => onApply(selectedFile)}
          disabled={!selectedId ||
    selectedFile?.status === "PENDING"}
        />
      </div>
    </WhiteModal>
  );
};

export default ApplyResumePopup;